import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Layout,
  Typography,
  Button,
  List,
  Spin,
  Progress,
  Card,
  Alert,
  Space,
  Tag,
  Tooltip,
  Row,
  Col,
  Grid,
  Modal,
  Input,
  Form,
  message,
  Drawer,
  Menu,
  Checkbox,
  Select,
  Divider,
  Breadcrumb,
  Affix,
  Switch,
  Tabs,
} from 'antd';
import {
  LoadingOutlined,
  CheckOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  LockOutlined,
  ExportOutlined,
  MenuOutlined,
  FilterOutlined,
  RightOutlined,
  FileSearchOutlined,
  SortAscendingOutlined,
  DashboardOutlined,
  FormOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  SettingOutlined,
  FileTextOutlined,
  LineChartOutlined,
  UserOutlined,
  SaveOutlined,
  FileWordOutlined,
} from '@ant-design/icons';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import Editor from './Editor';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import './FactCheckDetail.css';
import moment from 'moment-timezone';
import { calculateCharacterCount } from '../../utils/calculateCharacterCount';
import { formatDateTime } from '../../utils/dateUtils';
import { getUserProfile } from '../../auth';
import AuthenticatedMenu from '../AuthenticatedMenu';
import { setAuthToken } from '../../api';

// momentのデフォルトタイムゾーンを日本時間に設定
moment.tz.setDefault('Asia/Tokyo');

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

// タイプライター効果コンポーネント（FactCheckDetailから再利用）
const TypewriterText = ({
  text,
  speed = 40,
  onComplete,
  dangerouslySetInnerHTML = false,
}) => {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const timerRef = useRef(null);
  const hasCalledOnCompleteRef = useRef(false);
  const startTimeRef = useRef(Date.now());
  const textLengthRef = useRef(0);
  const textRef = useRef(text); // テキスト参照を追加

  // HTMLからテキストを抽出
  const getPlainTextFromHTML = useCallback(htmlText => {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlText;
      return tempDiv.textContent || tempDiv.innerText || '';
    } catch (e) {
      console.error('[ERROR-TYPEWRITER] HTMLテキスト処理エラー:', e);
      return '';
    }
  }, []);

  // タイプライター処理のリセット
  const resetTypewriter = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    hasCalledOnCompleteRef.current = false;
    setDisplayText('');
    setIsComplete(false);
    startTimeRef.current = Date.now();
  }, []);

  // 完了コールバックの安全な呼び出し
  const safeCallOnComplete = useCallback(() => {
    if (!hasCalledOnCompleteRef.current && onComplete) {
      hasCalledOnCompleteRef.current = true;
      onComplete();
    }
  }, [onComplete]);

  // テキストが変更されたら更新
  useEffect(() => {
    textRef.current = text || '';
  }, [text]);

  // タイプライターアニメーション実行
  useEffect(() => {
    // 入力テキストが変更されたらリセット
    resetTypewriter();

    // 処理するテキストを取得
    const plainText = dangerouslySetInnerHTML
      ? getPlainTextFromHTML(textRef.current)
      : textRef.current || '';

    textLengthRef.current = plainText.length;

    // テキストがない場合は即座に完了
    if (!plainText || plainText.length === 0) {
      setIsComplete(true);
      safeCallOnComplete();
      return;
    }

    // 短いテキストは高速表示、長いテキストはさらに高速化
    let actualSpeed = speed;
    if (plainText.length > 200) {
      actualSpeed = Math.max(5, speed / 4); // 非常に長いテキスト
    } else if (plainText.length > 100) {
      actualSpeed = Math.max(10, speed / 2); // 長いテキスト
    }

    // インデックスとタイマー
    let index = 0;

    // タイプライター処理関数
    const typeNextChar = () => {
      if (index < plainText.length) {
        setDisplayText(plainText.substring(0, index + 1));
        index++;

        // 次の文字表示をスケジュール
        timerRef.current = setTimeout(typeNextChar, actualSpeed);
      } else {
        // タイプライター完了
        setIsComplete(true);
        timerRef.current = null;

        // 即座に完了コールバックを呼び出す（安全なディレイを保持）
        setTimeout(() => {
          safeCallOnComplete();
        }, 50);
      }
    };

    // 処理開始
    timerRef.current = setTimeout(typeNextChar, 50);

    // クリーンアップ関数
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      // unmount時に完了していなければコールバック呼び出し
      if (!isComplete) {
        safeCallOnComplete();
      }
    };
  }, [
    textRef.current,
    speed,
    dangerouslySetInnerHTML,
    getPlainTextFromHTML,
    safeCallOnComplete,
    resetTypewriter,
    isComplete,
  ]);

  // 表示用のHTMLまたはテキスト
  return dangerouslySetInnerHTML ? (
    <div dangerouslySetInnerHTML={{ __html: displayText }} />
  ) : (
    <>{displayText}</>
  );
};

// アニメーション付き数値表示コンポーネント
const AnimatedNumber = ({ value, duration = 1000, formatter = val => val }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const startTimeRef = useRef(null);
  const startValueRef = useRef(0);
  const frameRef = useRef(null);

  useEffect(() => {
    startValueRef.current = displayValue;
    startTimeRef.current = performance.now();

    const animate = timestamp => {
      const elapsed = timestamp - startTimeRef.current;
      if (elapsed < duration) {
        const nextValue =
          startValueRef.current +
          ((value - startValueRef.current) * elapsed) / duration;
        setDisplayValue(nextValue);
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
      }
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [value, duration]);

  return <span>{formatter(Math.round(displayValue))}</span>;
};

// スケルトンローディングコンポーネント
const FragmentSkeleton = ({ count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="fragment-card skeleton">
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                position: 'relative',
              }}
            >
              {/* 円グラフのスケルトン */}
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: '#f0f0f0',
                  flexShrink: 0,
                }}
              />
              {/* テキストのスケルトン */}
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    height: '16px',
                    background: '#f0f0f0',
                    borderRadius: '4px',
                    width: '100%',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

// 正確性に基づく色分けロジック
const getAccuracyColor = accuracy => {
  if (accuracy >= 90) return '#0072FF';
  if (accuracy >= 80) return '#2E8CFF';
  if (accuracy >= 70) return '#52C41A';
  if (accuracy >= 60) return '#73D13D';
  if (accuracy >= 50) return '#FFA940';
  if (accuracy >= 40) return '#FA8C16';
  if (accuracy >= 30) return '#FA541C';
  return '#F5222D';
};

// リンクを青色に変更する関数
const formatReasonWithLinks = text => {
  if (!text) return '';

  // まずURLを検出して青色のリンクに変換
  let formattedText = text.replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: underline;">$1</a>',
  );

  // 次に[n]形式の参照を青色のスパンに変換
  formattedText = formattedText.replace(
    /\[(\d+)\]/g,
    '<span style="color: #1890ff; cursor: pointer; font-weight: 500;" class="reference-link-number" data-ref-number="$1" onclick="window.openReferenceUrl($1)">[<span>$1</span>]</span>',
  );

  return formattedText;
};

const SharedFactCheck = () => {
  const { task_id } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  // 状態管理
  const [task, setTask] = useState(null);
  const [editorContent, setEditorContent] = useState(null);
  const [fragments, setFragments] = useState([]);
  const [displayedFragments, setDisplayedFragments] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    accuracy: 0,
    sourceCount: 0,
  });
  const [editorReady, setEditorReady] = useState(false);
  const [drawerState, setDrawerState] = useState({
    visible: false,
    fragment: null,
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [userRole, setUserRole] = useState('user');
  // ticketCount は廃止（クレジット制に移行）

  // パスワード保護関連の状態
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // 表示スタイル関連
  const [hideBranding, setHideBranding] = useState(false);
  const [cardMaxHeight, setCardMaxHeight] = useState('calc(100vh - 100px)');

  // スケルトンカード表示の状態
  const [forceHideSkeleton, setForceHideSkeleton] = useState(false);

  // その他の状態
  const [selectedFragment, setSelectedFragment] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const ticking = useRef(false);
  const lastMaxHeightRef = useRef('calc(100vh - 100px)');
  // 自動スクロール下限を保持
  const [minScrollPosition, setMinScrollPosition] = useState(88);

  // 自動スクロール機能
  const [autoScroll, setAutoScroll] = useState(false); // デフォルトで無効化

  // フィルターと並び替えの状態管理
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [sortOrder, setSortOrder] = useState('default');
  // 登録モーダル用ステート
  const [isModalVisible, setIsModalVisible] = useState(false);

  // アニメーション用クラスをhead内に追加
  useEffect(() => {
    const shimmerStyle = document.createElement('style');
    shimmerStyle.textContent = `
      @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `;
    document.head.appendChild(shimmerStyle);

    return () => {
      if (document.head.contains(shimmerStyle)) {
        document.head.removeChild(shimmerStyle);
      }
    };
  }, []);

  // スクロール位置を監視して、フッター付近でカードの最大高さを調整する
  useEffect(() => {
    let footerElement = null;

    const handleScroll = () => {
      // ワンカラム表示時（スマホ等）はAffixを適用しない
      if (!screens.md) {
        return;
      }

      // スクロールイベントの発火頻度を抑制する
      if (!ticking.current) {
        // 次の描画フレームで実行するようスケジュール
        window.requestAnimationFrame(() => {
          // フッター要素がない場合は取得（初回のみ）
          if (!footerElement) {
            footerElement = document.querySelector('footer');
          }

          const windowHeight = window.innerHeight;
          const scrollPosition = window.scrollY + windowHeight;

          // フッター位置を計算（キャッシュした要素を使用）
          const footerPosition = footerElement
            ? footerElement.offsetTop
            : document.body.scrollHeight - 100;

          const footerHeight = 149; // フッターの高さ

          // スクロール位置がフッター付近まで来たら
          if (scrollPosition > footerPosition - footerHeight) {
            // フッターがどれだけ表示されているかを計算（整数値に丸める）
            const visibleFooterHeight = Math.min(
              footerHeight,
              Math.ceil(scrollPosition - (footerPosition - footerHeight)),
            );

            // カードの最大高さを調整（計算を最適化）
            const newMaxHeight = `calc(100vh - 100px - ${visibleFooterHeight}px)`;

            // 前回と異なる場合のみ更新（無駄な再レンダリングを防止）
            if (lastMaxHeightRef.current !== newMaxHeight) {
              lastMaxHeightRef.current = newMaxHeight;
              setCardMaxHeight(newMaxHeight);
            }
          } else if (lastMaxHeightRef.current !== 'calc(100vh - 100px)') {
            // 通常の最大高さ（変更がある場合のみ更新）
            lastMaxHeightRef.current = 'calc(100vh - 100px)';
            setCardMaxHeight('calc(100vh - 100px)');
          }

          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    // パッシブイベントリスナーとして登録（スクロールパフォーマンス向上）
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 初期表示時にも実行
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [screens.md]);

  // パスワード検証のハンドラー
  const handlePasswordSubmit = async () => {
    setPasswordLoading(true);
    setPasswordError('');
    try {
      const ok = await fetchData(passwordInput);
      if (!ok) {
        setPasswordError('パスワードが正しくありません。');
      }
    } catch (e) {
      setPasswordError('パスワードが正しくありません。');
    } finally {
      setPasswordLoading(false);
    }
  };

  // 統計情報計算
  const calculateStats = useCallback(
    fragmentList => {
      if (!fragmentList || fragmentList.length === 0) {
        return {
          total: 0,
          verified: 0,
          accuracy: 0,
          sourceCount: 0,
          completed: 0,
        };
      }

      // 検証完了しているフラグメントのみをフィルタリング
      const verifiedFragments = fragmentList.filter(
        frag => frag.factuality !== undefined && frag.factuality !== null,
      );

      // 手動で完了済みにしたフラグメントの数をカウント
      const completedFragments = fragmentList.filter(
        frag => frag.result === true,
      );
      const completedCount = completedFragments.length;

      // 検証完了フラグメントの数と総フラグメント数
      let total = fragmentList.length;
      let verifiedCount = verifiedFragments.length;

      // 検証完了フラグメントのみで平均factualityを計算
      let sumFactuality = verifiedFragments.reduce(
        (sum, frag) => sum + (frag.factuality || 0),
        0,
      );

      // ソース数のカウント（検証完了フラグメントのみ）
      let totalSourceCount = verifiedFragments.reduce((count, frag) => {
        // reference_infoのソースをカウント
        const refCount = Array.isArray(frag.reference_info)
          ? frag.reference_info.length
          : 0;

        // deep_check_sourcesのソースをカウント
        const deepCheckCount = Array.isArray(frag.deep_check_sources)
          ? frag.deep_check_sources.length
          : 0;

        return count + refCount + deepCheckCount;
      }, 0);

      // factualityの平均を計算（検証完了フラグメントがある場合のみ）
      const avgFactuality =
        verifiedCount > 0 ? sumFactuality / verifiedCount : 0;
      const accuracy = Math.round(avgFactuality * 100);

      return {
        total,
        verified: verifiedCount,
        accuracy,
        sourceCount: totalSourceCount,
        completed: completedCount,
      };
    },
    [task_id],
  );

  // データ取得関数
  const fetchData = useCallback(
    async (providedPassword = null) => {
      let success = false;
      try {
        setIsLoading(true);

        const token = localStorage.getItem('token');
        if (token) {
          setAuthToken(token);
          try {
            const profile = await getUserProfile();
            setIsAuthenticated(true);
            setUserProfile(profile);
            setUserRole(profile.role);
          } catch (error) {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }

        let url = `${process.env.REACT_APP_API_URL}/fact-check/shared/fact-check/${task_id}`;
        const headers = {};

        if (providedPassword) {
          url += `?password=${encodeURIComponent(providedPassword)}`;
        }

        const resp = await axios.get(url, { headers });
        const data = resp.data;

        // まず data.task が存在するか確認
        if (data && data.task) {
          setTask(data.task);
          setHideBranding(data.task.hide_branding || false);
        } else if (!data.password_required) {
          // task がなく、パスワードも要求されない場合はエラーの可能性
          setError('タスクデータの形式が正しくありません。');
          setIsLoading(false);
          return false; // 早期リターン
        }

        if (data.fact_check_text) {
          try {
            const content = JSON.parse(data.fact_check_text);
            setEditorContent(content);
          } catch (e) {
            message.error('コンテンツの解析に失敗しました');
          }
        }

        // パスワードが提供されておらず、かつAPIがパスワードを要求してきた場合
        if (data.password_required) {
          setPasswordRequired(true);
          if (providedPassword) {
            // パスワード入力試行があった場合
            setPasswordError('パスワードが正しくありません。');
          }
          // 認証関連のstateをリセット
          setIsAuthenticated(false);
          setUserProfile(null);
          setIsLoading(false);
          return false; // 認証が必要なため、ここで処理を終了
        }

        // パスワード不要または正しい場合 (この時点で data.task は設定済みのはず)
        setPasswordRequired(false);
        // task は既に設定されているはずなので、ここでは再設定の必要なし

        if (Array.isArray(data.fragments)) {
          const fragsWithId = data.fragments.map(f => ({
            ...f,
            id: f.task_id + '-' + f.order_index,
          }));
          setFragments(fragsWithId);
          const verifiedFragments = fragsWithId.filter(
            frag => frag.factuality !== undefined && frag.factuality !== null,
          );
          setDisplayedFragments(
            verifiedFragments.map(frag => ({
              ...frag,
              isTyping: false,
              isComplete: true,
              isInteractive: true,
            })),
          );
        }
        setForceHideSkeleton(true);
        success = true;
      } catch (error) {
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 403) &&
          providedPassword
        ) {
          setError('パスワードが正しくないか、アクセス権がありません。');
          setPasswordRequired(true);
        } else if (
          error.response &&
          error.response.data &&
          error.response.data.password_required
        ) {
          // APIから明示的にパスワード要求が返ってきた場合も表示
          setPasswordRequired(true);
          setIsAuthenticated(false);
          setUserProfile(null);
        } else {
          setError('データの取得に失敗しました');
        }
      } finally {
        setIsLoading(false);
      }
      return success;
    },
    [task_id],
  );

  // 初期データ取得
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // フラグメントの変更を監視して統計情報を更新
  useEffect(() => {
    if (!fragments.length) {
      setStats({
        total: 0,
        verified: 0,
        accuracy: 0,
        sourceCount: 0,
      });
      return;
    }

    const newStats = calculateStats(fragments);
    setStats(newStats);
  }, [fragments, calculateStats]);

  // エディタの参照
  const editorRef = useRef(null);

  // エディタの準備完了時の処理を修正
  const handleEditorReady = () => {
    const editor = window._editor;
    if (!editor) {
      return;
    }
    editorRef.current = editor;
    setEditorReady(true);

    // エディタの初期化後にブロック番号を更新
    setTimeout(() => {
      updateBlockNumbers();
    }, 500);

    // results-panel-affix の位置を取得し、自動スクロール下限を計算
    setTimeout(() => {
      const affixElement = document.querySelector('.results-panel-affix');
      if (affixElement) {
        const pos =
          affixElement.getBoundingClientRect().top + window.pageYOffset - 24;
        setMinScrollPosition(pos);
      }
    }, 100);
  };

  // ドロワーを開く処理
  const handleDrawerOpen = useCallback(fragment => {
    if (!fragment) return;
    setDrawerState(prev => ({
      visible: true,
      fragment: { ...fragment },
    }));

    // フラグメントとブロックのハイライトを設定
    const fragmentCard = document.querySelector(
      `.fragment-card[data-fragment-id="${fragment.id}"]`,
    );
    if (fragmentCard) {
      fragmentCard.classList.add('selected');
    }
  }, []);

  // ドロワーを閉じる処理
  const handleDrawerClose = useCallback(() => {
    setDrawerState(prev => ({
      visible: false,
      fragment: null,
    }));

    // 全てのハイライトを削除
    document.querySelectorAll('.fragment-card.selected').forEach(el => {
      el.classList.remove('selected');
    });
  }, []);

  // フラグメントカードクリック時の処理
  const handleFragmentClick = useCallback(fragment => {
    setSelectedFragment(fragment);
    setIsDrawerOpen(true);
  }, []);

  // ホバー処理
  const handleHeaderHover = useCallback(
    (isHovering, blockIds, shouldScroll = false) => {
      if (!isHovering) {
        document
          .querySelectorAll('.block-hover-highlight')
          .forEach(el => el.classList.remove('block-hover-highlight'));
        return;
      }

      if (isHovering && blockIds?.length) {
        const firstBlockId = blockIds[0];
        const block = editorRef.current?.blocks.getById(firstBlockId);

        if (block && block.holder) {
          block.holder.classList.add('block-hover-highlight');

          // 自動スクロールがON、かつshouldScrollがtrueの場合のみスクロール
          if (autoScroll && shouldScroll) {
            const rect = block.holder.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const centerPosition =
              rect.top +
              window.pageYOffset -
              windowHeight / 2 +
              rect.height / 2;
            const targetPosition = Math.max(minScrollPosition, centerPosition);

            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth',
            });
          }
        }
      }
    },
    [autoScroll, editorRef, minScrollPosition],
  );

  // ブロック番号の更新関数を修正
  const updateBlockNumbers = useCallback(() => {
    if (!editorRef.current || !fragments.length) return;

    // 既存の番号表示をクリア
    document.querySelectorAll('.block-numbers').forEach(el => el.remove());

    // ブロックごとのフラグメント番号をマッピング
    const blockFragments = {};
    fragments.forEach(fragment => {
      if (fragment.block_position?.ids) {
        fragment.block_position.ids.forEach(blockId => {
          if (!blockFragments[blockId]) {
            blockFragments[blockId] = [];
          }
          blockFragments[blockId].push({
            number: fragment.order_index + 1,
            factuality: fragment.factuality || 0,
            result: fragment.result || false,
            deep_check_status: fragment.deep_check_status,
            // フラグメント全体の情報を保持
            fragment: fragment,
          });
        });
      }
    });

    // 各ブロックに番号を表示
    Object.entries(blockFragments).forEach(([blockId, fragmentsInfo]) => {
      // EditorJSのAPIを使用してブロックを取得
      const block = editorRef.current?.blocks.getById(blockId);
      if (block) {
        const holder = block.holder;
        if (!holder) return;

        const contentElement = holder.querySelector('.ce-block__content');
        if (!contentElement) return;

        const numbersContainer = document.createElement('div');
        numbersContainer.className = 'block-numbers';

        fragmentsInfo
          .sort((a, b) => a.number - b.number)
          .forEach(fragmentInfo => {
            if (showIncomplete && fragmentInfo.result) {
              return;
            }

            const badge = document.createElement('div');
            badge.className = `block-number`;
            badge.textContent = fragmentInfo.number;

            // 判定：factuality が設定されている＝検証済みかどうか
            const isVerified =
              fragmentInfo.fragment.factuality !== undefined &&
              fragmentInfo.fragment.factuality !== null;

            // リロード時も未検証状態を維持
            badge.style.backgroundColor = '#d9d9d9';
            badge.style.color = '#888888';
            badge.style.cursor = 'default';

            if (isVerified) {
              // 検証済みの場合のスタイル適用
              if (fragmentInfo.deep_check_status === 'completed') {
                badge.style.background =
                  'linear-gradient(45deg, #722ed1, #eb2f96)';
              } else {
                const reliability = fragmentInfo.factuality * 100;
                const reliabilityColor = getAccuracyColor(reliability);
                badge.style.backgroundColor = reliabilityColor;
              }
              badge.style.color = 'white';

              // 検証済みの場合のみ、イベントリスナーを登録
              badge.style.cursor = 'pointer';
              badge.addEventListener('click', e => {
                e.stopPropagation();
                handleDrawerOpen(fragmentInfo.fragment);
              });
              badge.addEventListener('mouseenter', () =>
                handleHeaderHover(
                  true,
                  fragmentInfo.fragment.block_position?.ids,
                  false,
                ),
              );
              badge.addEventListener('mouseleave', () =>
                handleHeaderHover(false),
              );
            }

            if (!showIncomplete && fragmentInfo.result) {
              badge.style.opacity = '0.3';
            }

            if (fragmentInfo.result) {
              const checkIcon = document.createElement('div');
              checkIcon.className = 'block-number-check';
              checkIcon.innerHTML = `<svg viewBox="64 64 896 896" focusable="false" data-icon="check" width="1em" height="1em" fill="currentColor" aria-hidden="true" stroke="currentColor" stroke-width="50"><path d="M912 190h-69.9c-9.8 0-19.1 4.5-25.1 12.2L404.7 724.5 207 474a32 32 0 00-25.1-12.2H112c-6.7 0-10.4 7.7-6.3 12.9l273.9 347c12.8 16.2 37.4 16.2 50.3 0l488.4-618.9c4.1-5.1.4-12.8-6.3-12.8z"></path></svg>`;
              badge.appendChild(checkIcon);
            }

            numbersContainer.appendChild(badge);
          });

        contentElement.style.position = 'relative';
        contentElement.appendChild(numbersContainer);
      }
    });
  }, [
    fragments,
    showIncomplete,
    handleDrawerOpen,
    handleHeaderHover,
    editorRef,
    minScrollPosition,
  ]);

  // フラグメントの変更を監視してブロック番号を更新
  useEffect(() => {
    if (editorReady && fragments.length > 0) {
      updateBlockNumbers();
    }
  }, [fragments, editorReady, updateBlockNumbers]);

  // showRegistrationModal関数を追加
  const showRegistrationModal = () => {
    setIsModalVisible(true);
  };

  // handleRegistration関数を追加
  const handleRegistration = () => {
    const utmParams = JSON.parse(localStorage.getItem('utmParams') || '{}');
    const searchParams = new URLSearchParams(utmParams);
    navigate(`/login?${searchParams.toString()}`);
  };

  // フィルターセクション
  const FilterSection = () => {
    return (
      <>
        <div style={{ marginTop: '16px' }}>
          <Row align="middle" justify="space-between">
            <Col>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                  borderLeft: '3px solid #00d4ff',
                  paddingLeft: '10px',
                }}
              >
                検証項目一覧
                <Tooltip title="情報を表示">
                  <InfoCircleOutlined
                    style={{
                      color: '#1890ff',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      Modal.info({
                        title: 'ファクトチェックについて',
                        width: 900,
                        maskClosable: true,
                        content: (
                          <div
                            style={{
                              marginTop: '16px',
                              maxHeight: 'calc(80vh - 100px)',
                              overflow: 'auto',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#e6e6e6 transparent',
                              msOverflowStyle: 'none',
                              '&::-webkit-scrollbar': {
                                width: '6px',
                                height: '6px',
                              },
                              '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                background: '#e6e6e6',
                                borderRadius: '3px',
                              },
                              '&::-webkit-scrollbar-thumb:hover': {
                                background: '#d9d9d9',
                              },
                            }}
                          >
                            <Tabs defaultActiveKey="1">
                              <Tabs.TabPane tab="検証方法" key="1">
                                <div style={{ padding: '8px 0' }}>
                                  <p>
                                    AIが原稿を分析し、事実確認が必要な記述を自動的に抽出します。
                                    抽出される項目数は原稿の長さに応じて調整されます。
                                  </p>

                                  <div
                                    style={{
                                      background: '#f9f9f9',
                                      padding: '12px',
                                      borderRadius: '8px',
                                      marginTop: '12px',
                                      marginBottom: '16px',
                                      border: '1px solid #f0f0f0',
                                    }}
                                  >
                                    <div
                                      style={{
                                        marginBottom: '8px',
                                        fontWeight: '500',
                                      }}
                                    >
                                      抽出の優先順位：
                                    </div>
                                    <ol
                                      style={{
                                        paddingLeft: '20px',
                                        margin: '0',
                                      }}
                                    >
                                      <li>
                                        誤情報になった場合に重大なリスクがある主張
                                      </li>
                                      <li>
                                        商品やサービスの機能や特徴、統計など一次情報が必要な主張
                                      </li>
                                      <li>
                                        専門的な情報で、信頼性の高い裏付けが必要な主張
                                      </li>
                                      <li>
                                        情報の根拠が併記されていない、または根拠が薄い主張
                                      </li>
                                    </ol>
                                  </div>

                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      marginBottom: '16px',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        background: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '12px',
                                        fontSize: '20px',
                                        color: '#1890ff',
                                      }}
                                    >
                                      <FileSearchOutlined />
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          fontWeight: '500',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        文字数に応じた検証項目数（従量制）
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '12px',
                                          color: '#666',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        文字数に応じて細かくクレジットが変動します。
                                      </div>

                                      <div
                                        style={{
                                          fontSize: '11px',
                                          color: '#999',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        ▼ サンプル値
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '13px',
                                          lineHeight: '1.5',
                                        }}
                                      >
                                        〜1,000文字：15項目 / 20クレジット
                                        <br />
                                        〜3,000文字：34項目 / 39クレジット
                                        <br />
                                        〜5,000文字：54項目 / 57クレジット
                                        <br />
                                        〜8,000文字：83項目 / 85クレジット
                                        <br />
                                        〜11,000文字：111項目 / 113クレジット
                                        <br />
                                        〜15,000文字：150項目 / 150クレジット
                                      </div>
                                    </div>
                                  </div>

                                  <Alert
                                    message={
                                      <span
                                        style={{
                                          fontSize: '14px',
                                          fontWeight: '700',
                                        }}
                                      >
                                        検証項目は原稿の内容に基づいて抽出されます
                                      </span>
                                    }
                                    description="原稿の内容によっては、抽出される項目数が上記の目安と異なる場合があります。事実検証すべき内容が少ない原稿では、抽出項目が少なくなります。"
                                    type="info"
                                    showIcon
                                  />
                                </div>
                              </Tabs.TabPane>

                              <Tabs.TabPane tab="事実性スコア" key="2">
                                <div style={{ padding: '8px 0' }}>
                                  <p>
                                    事実性スコアは、記述の事実的正確性を0.0〜1.0の数値で表します。
                                    スコアが高いほど、信頼できる情報源によって裏付けられていることを意味します。
                                  </p>

                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      marginTop: '20px',
                                      marginBottom: '24px',
                                    }}
                                  >
                                    <div style={{ textAlign: 'center' }}>
                                      <Progress
                                        type="circle"
                                        percent={95}
                                        width={60}
                                        strokeColor="#1890ff"
                                      />
                                      <div
                                        style={{
                                          marginTop: '8px',
                                          fontWeight: '500',
                                        }}
                                      >
                                        高い信頼性
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '12px',
                                          color: 'rgba(0, 0, 0, 0.45)',
                                        }}
                                      >
                                        0.9〜1.0
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <Progress
                                        type="circle"
                                        percent={75}
                                        width={60}
                                        strokeColor="#52c41a"
                                      />
                                      <div
                                        style={{
                                          marginTop: '8px',
                                          fontWeight: '500',
                                        }}
                                      >
                                        一定の信頼性
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '12px',
                                          color: 'rgba(0, 0, 0, 0.45)',
                                        }}
                                      >
                                        0.7〜0.89
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <Progress
                                        type="circle"
                                        percent={55}
                                        width={60}
                                        strokeColor="#faad14"
                                      />
                                      <div
                                        style={{
                                          marginTop: '8px',
                                          fontWeight: '500',
                                        }}
                                      >
                                        確認が必要
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '12px',
                                          color: 'rgba(0, 0, 0, 0.45)',
                                        }}
                                      >
                                        0.5〜0.69
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <Progress
                                        type="circle"
                                        percent={35}
                                        width={60}
                                        strokeColor="#ff4d4f"
                                      />
                                      <div
                                        style={{
                                          marginTop: '8px',
                                          fontWeight: '500',
                                        }}
                                      >
                                        低い信頼性
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '12px',
                                          color: 'rgba(0, 0, 0, 0.45)',
                                        }}
                                      >
                                        0.1〜0.49
                                      </div>
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      background: '#f9f9f9',
                                      padding: '12px',
                                      borderRadius: '8px',
                                      marginBottom: '16px',
                                      border: '1px solid #f0f0f0',
                                    }}
                                  >
                                    <div
                                      style={{
                                        marginBottom: '8px',
                                        fontWeight: '500',
                                      }}
                                    >
                                      スコア計算の主な要素：
                                    </div>
                                    <ul
                                      style={{
                                        paddingLeft: '20px',
                                        margin: '0',
                                      }}
                                    >
                                      <li>
                                        <strong>情報源の信頼性</strong>
                                        ：政府機関や学術機関のサイトは高評価
                                      </li>
                                      <li>
                                        <strong>情報の関連性</strong>
                                        ：検証項目と引用情報の直接的な関連性
                                      </li>
                                      <li>
                                        <strong>複数ソースの一致</strong>
                                        ：複数の情報源による裏付け
                                      </li>
                                      <li>
                                        <strong>情報の新しさ</strong>
                                        ：最新の情報ほど高評価
                                      </li>
                                    </ul>
                                  </div>

                                  <Alert
                                    message={
                                      <span
                                        style={{
                                          fontSize: '14px',
                                          fontWeight: '700',
                                        }}
                                      >
                                        低スコアの項目は要注意
                                      </span>
                                    }
                                    description="スコアが低い項目は、情報の裏付けが不十分か、矛盾する情報が見つかった可能性があります。Sonar検証を実行するか、手動で情報を確認することをお勧めします。"
                                    type="warning"
                                    showIcon
                                  />
                                </div>
                              </Tabs.TabPane>

                              <Tabs.TabPane tab="使い方のヒント" key="4">
                                <div style={{ padding: '8px 0' }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      marginBottom: '16px',
                                      padding: '12px',
                                      background: '#f9f9f9',
                                      borderRadius: '8px',
                                      border: '1px solid #f0f0f0',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: '#e6f7ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '12px',
                                        fontSize: '16px',
                                        color: '#1890ff',
                                        flexShrink: 0,
                                        marginTop: '4px',
                                      }}
                                    >
                                      <FilterOutlined />
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          fontWeight: '500',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        フィルタリング機能
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '13px',
                                          marginBottom: '8px',
                                        }}
                                      >
                                        「未完了のみを表示」チェックボックスを使うと、まだ確認していない項目だけを表示できます。
                                      </div>
                                      <div
                                        style={{
                                          border: '1px solid #d9d9d9',
                                          borderRadius: '4px',
                                          padding: '8px',
                                          background: 'white',
                                          fontSize: '13px',
                                        }}
                                      >
                                        <Checkbox checked={true}>
                                          未完了のみを表示
                                        </Checkbox>
                                      </div>
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      marginBottom: '16px',
                                      padding: '12px',
                                      background: '#f9f9f9',
                                      borderRadius: '8px',
                                      border: '1px solid #f0f0f0',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: '#e6f7ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '12px',
                                        fontSize: '16px',
                                        color: '#1890ff',
                                        flexShrink: 0,
                                        marginTop: '4px',
                                      }}
                                    >
                                      <SortAscendingOutlined />
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          fontWeight: '500',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        ソート機能
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '13px',
                                          marginBottom: '8px',
                                        }}
                                      >
                                        ドロップダウンメニューで項目の表示順を変更できます。スコアの低い項目から確認すると効率的です。
                                      </div>
                                      <div
                                        style={{
                                          border: '1px solid #d9d9d9',
                                          borderRadius: '4px',
                                          padding: '8px',
                                          background: 'white',
                                          fontSize: '13px',
                                        }}
                                      >
                                        <Select
                                          defaultValue="low"
                                          style={{ width: 150 }}
                                          options={[
                                            {
                                              value: 'default',
                                              label: 'デフォルト',
                                            },
                                            {
                                              value: 'low',
                                              label: 'スコアの低い順',
                                            },
                                            {
                                              value: 'high',
                                              label: 'スコアの高い順',
                                            },
                                          ]}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <Alert
                                    message={
                                      <span
                                        style={{
                                          fontSize: '14px',
                                          fontWeight: '700',
                                        }}
                                      >
                                        自動スクロール機能
                                      </span>
                                    }
                                    description="検証項目にマウスを乗せると、原稿の対応する箇所へ自動スクロールします。自動スクロールのトグルスイッチから機能のオン/オフを切り替えられます。"
                                    type="info"
                                    showIcon
                                  />
                                </div>
                              </Tabs.TabPane>
                            </Tabs>
                          </div>
                        ),
                        okText: '閉じる',
                      });
                    }}
                  />
                </Tooltip>
              </div>
            </Col>
            <Col>
              <Tooltip title="検証項目にマウスを乗せた時に、原稿の対応する箇所へ自動スクロールします">
                <Switch
                  checked={autoScroll}
                  onChange={checked => setAutoScroll(checked)}
                  checkedChildren="自動スクロール"
                  unCheckedChildren="自動スクロール"
                />
              </Tooltip>
            </Col>
          </Row>

          <Row
            align="middle"
            justify="space-between"
            style={{
              padding: '8px 16px',
              backgroundColor: '#fafafa',
              marginBottom: '8px',
              flexDirection: screens.sm ? 'row' : 'column',
              alignItems: screens.sm ? 'center' : 'flex-start',
              gap: screens.sm ? 0 : '12px',
              width: '100%',
            }}
          >
            <Col
              xs={24}
              sm={12}
              style={{ marginBottom: screens.sm ? 0 : '8px' }}
            >
              <Tooltip title="未完了の検証項目のみを表示します。完了済みの項目は非表示になります。">
                <Checkbox
                  checked={showIncomplete}
                  onChange={e => setShowIncomplete(e.target.checked)}
                >
                  未完了のみを表示
                </Checkbox>
              </Tooltip>
            </Col>
            <Col
              xs={24}
              sm={12}
              style={{
                textAlign: screens.sm ? 'right' : 'left',
                width: '100%',
              }}
            >
              <Tooltip title="検証項目の表示順序を変更します。スコアの低い項目から確認すると効率的です。">
                <Select
                  value={sortOrder}
                  onChange={value => setSortOrder(value)}
                  style={{ width: screens.sm ? 150 : '100%' }}
                  options={[
                    { value: 'default', label: 'デフォルト' },
                    { value: 'low', label: 'スコアの低い順' },
                    { value: 'high', label: 'スコアの高い順' },
                  ]}
                />
              </Tooltip>
            </Col>
          </Row>
        </div>
      </>
    );
  };

  // 並び替え済みフラグメント取得関数
  const getFilteredAndSortedFragments = useCallback(() => {
    // まず未検証フラグメントを除外
    let result = fragments.filter(
      f => f.factuality !== undefined && f.factuality !== null,
    );

    // 未完了のみ表示フィルター
    if (showIncomplete) {
      result = result.filter(f => !f.result);
    }

    // 並び替え
    switch (sortOrder) {
      case 'low':
        result.sort((a, b) => (a.factuality || 0) - (b.factuality || 0));
        break;
      case 'high':
        result.sort((a, b) => (b.factuality || 0) - (a.factuality || 0));
        break;
      default:
        result.sort((a, b) => a.order_index - b.order_index);
    }

    return result;
  }, [fragments, showIncomplete, sortOrder]);

  const handleToggleComplete = useCallback(
    (fragment, isComplete) => {
      setFragments(prevFragments =>
        prevFragments.map(f =>
          f.id === fragment.id ? { ...f, result: isComplete } : f,
        ),
      );
    },
    [setFragments],
  );

  // ロード中表示
  if (isLoading) {
    return (
      <div className="loading-container">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <div className="error-container">
        <Alert
          message="エラーが発生しました"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  // パスワード入力フォーム
  if (passwordRequired) {
    return (
      <div
        className="password-container"
        style={{
          maxWidth: '400px',
          margin: '100px auto',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <Helmet>
          <title>パスワード保護 | ファクトチェック</title>
        </Helmet>

        <Card
          bordered={false}
          style={{
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ marginBottom: '24px', textAlign: 'center' }}>
            <LockOutlined
              style={{
                fontSize: '48px',
                color: '#1890ff',
                marginBottom: '16px',
              }}
            />
            <Title level={4}>パスワード保護されたコンテンツ</Title>
            <Text type="secondary">
              このコンテンツを閲覧するには、共有者から提供されたパスワードを入力してください。
            </Text>
          </div>

          <Form onFinish={handlePasswordSubmit}>
            <Form.Item
              validateStatus={passwordError ? 'error' : ''}
              help={passwordError || ''}
            >
              <Input.Password
                size="large"
                placeholder="パスワードを入力"
                value={passwordInput}
                onChange={e => setPasswordInput(e.target.value)}
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                block
                loading={passwordLoading}
              >
                確認
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>検証結果 | ファクトチェック - magicss</title>
        <meta name="robots" content="noindex" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Helmet>

      {hideBranding ? (
        // ロゴ非表示モード（シンプルレイアウト）
        <Layout
          style={{
            minHeight: '100vh',
            background: '#fff',
            padding: screens.md ? '24px' : '16px', // 上下パディングも統一
          }}
          className="hide-branding"
        >
          <div style={{ maxWidth: '1500px', margin: '0 auto', width: '100%' }}>
            <Breadcrumb style={{ margin: '16px 0' }}>
              <Breadcrumb.Item>ファクトチェック</Breadcrumb.Item>
              <Breadcrumb.Item>検証結果</Breadcrumb.Item>
            </Breadcrumb>
            <Row>
              <Col span={24}>
                <Row
                  justify="space-between"
                  align="middle"
                  gutter={[0, { xs: 24, sm: 0 }]}
                >
                  <Col xs={24} md={12}>
                    <Title
                      level={2}
                      style={{ margin: 0 }}
                      className="gradient-text"
                    >
                      検証結果
                    </Title>
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'rgba(0, 0, 0, 0.45)',
                        marginTop: '16px',
                      }}
                    >
                      <Space wrap>
                        <span>
                          実行日時： {formatDateTime(task?.created_at)}
                        </span>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    {/* 統計情報セクション */}
                    <Row gutter={[16, 16]} align="middle" wrap>
                      <Col xs={12} sm={6} md={6} lg={6}>
                        <div
                          style={{
                            textAlign: 'center',
                            height: screens.xs ? '60px' : '76px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div
                            style={{
                              fontSize: screens.xs ? '12px' : '13px',
                              color: 'rgba(0, 0, 0, 0.45)',
                              height: screens.xs ? '18px' : '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            文字数
                            <Tooltip title="検証対象の原稿の総文字数です。">
                              <QuestionCircleOutlined
                                style={{
                                  marginLeft: '4px',
                                  fontSize: '12px',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                }}
                              />
                            </Tooltip>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: screens.xs ? '36px' : '46px',
                            }}
                          >
                            <div
                              style={{
                                fontSize: screens.xs ? '18px' : '24px',
                                fontWeight: '500',
                                lineHeight: '1',
                                display: 'flex',
                                alignItems: 'flex-end',
                              }}
                            >
                              <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>
                                {calculateCharacterCount(
                                  editorContent,
                                ).toLocaleString() || '0'}
                              </span>
                              <span
                                style={{
                                  fontSize: screens.xs ? '12px' : '14px',
                                  marginLeft: '2px',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                  lineHeight: '1',
                                }}
                              >
                                文字
                              </span>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6} md={6} lg={6}>
                        <div
                          style={{
                            textAlign: 'center',
                            height: screens.xs ? '60px' : '76px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div
                            style={{
                              fontSize: screens.xs ? '12px' : '13px',
                              color: 'rgba(0, 0, 0, 0.45)',
                              height: screens.xs ? '18px' : '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            検証項目
                            <Tooltip title="AIが検出した検証すべき事実的な記述の数です。">
                              <QuestionCircleOutlined
                                style={{
                                  marginLeft: '4px',
                                  fontSize: '12px',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                }}
                              />
                            </Tooltip>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: screens.xs ? '36px' : '46px',
                            }}
                          >
                            <div
                              style={{
                                fontSize: screens.xs ? '18px' : '24px',
                                fontWeight: '500',
                                lineHeight: '1',
                                display: 'flex',
                                alignItems: 'flex-end',
                              }}
                            >
                              <span
                                style={{ color: '#8f8f8f', marginRight: '2px' }}
                              >
                                {stats.completed || 0}
                              </span>
                              <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>
                                {' '}
                                / {stats.total}
                              </span>
                              <span
                                style={{
                                  fontSize: screens.xs ? '12px' : '14px',
                                  marginLeft: '2px',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                  lineHeight: '1',
                                }}
                              >
                                件
                              </span>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6} md={6} lg={6}>
                        <div
                          style={{
                            textAlign: 'center',
                            height: screens.xs ? '60px' : '76px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div
                            style={{
                              fontSize: screens.xs ? '12px' : '13px',
                              color: 'rgba(0, 0, 0, 0.45)',
                              height: screens.xs ? '18px' : '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            検出ソース
                            <Tooltip title="検証に使用された情報源の数です。信頼性の高いソースほど優先的に使用されます。">
                              <QuestionCircleOutlined
                                style={{
                                  marginLeft: '4px',
                                  fontSize: '12px',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                }}
                              />
                            </Tooltip>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: screens.xs ? '36px' : '46px',
                            }}
                          >
                            <div
                              style={{
                                fontSize: screens.xs ? '18px' : '24px',
                                fontWeight: '500',
                                lineHeight: '1',
                                display: 'flex',
                                alignItems: 'flex-end',
                              }}
                            >
                              <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>
                                {stats.sourceCount}
                              </span>
                              <span
                                style={{
                                  fontSize: screens.xs ? '12px' : '14px',
                                  marginLeft: '2px',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                  lineHeight: '1',
                                }}
                              >
                                件
                              </span>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6} md={6} lg={6}>
                        <div
                          style={{
                            textAlign: 'center',
                            height: screens.xs ? '60px' : '76px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div
                            style={{
                              fontSize: screens.xs ? '12px' : '13px',
                              color: 'rgba(0, 0, 0, 0.45)',
                              height: screens.xs ? '18px' : '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            正確性
                            <Tooltip title="検証された項目の正確性スコアの平均値です。100に近いほど原稿の事実的な正確性が高いことを示します。">
                              <QuestionCircleOutlined
                                style={{
                                  marginLeft: '4px',
                                  fontSize: '12px',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                }}
                              />
                            </Tooltip>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Progress
                              type="circle"
                              percent={stats.accuracy}
                              size={screens.xs ? 36 : 46}
                              strokeWidth={screens.xs ? 6 : 8}
                              strokeColor={getAccuracyColor(stats.accuracy)}
                              format={percent => (
                                <span
                                  style={{
                                    fontSize: screens.xs ? '12px' : '15px',
                                    fontWeight: 'bold',
                                    color: getAccuracyColor(percent),
                                  }}
                                >
                                  {percent}
                                </span>
                              )}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>

          <div className="fact-check-content" style={{ margin: '24px 0' }}>
            <PanelGroup
              direction={screens.md ? 'horizontal' : 'vertical'}
              autoSaveId="fact-check-panels"
              defaultSizes={[60, 40]}
            >
              <Panel
                defaultSize={60}
                minSize={40}
                style={{
                  overflow: screens.md ? 'hidden' : 'visible',
                  ...(screens.md ? {} : { minHeight: '400px', flex: 'none' }),
                }}
              >
                <div className="editor-panel">
                  <Editor
                    key={`editor-${!(task?.share_status === 'password' && !passwordRequired)}`}
                    initialData={editorContent}
                    onReady={handleEditorReady}
                    config={{
                      readOnly: !(
                        task?.share_status === 'password' && !passwordRequired
                      ),
                      holder: 'fact-check-editor',
                      minHeight: screens.md ? 0 : 350,
                      placeholder: '本文',
                      inlineToolbar: [],
                      tools: {},
                      // ブロック番号表示用の設定追加
                      blockRenderSettings: {
                        useID: true, // ブロックにIDを設定
                        preserveIDs: true, // IDを保持する
                      },
                    }}
                  />
                </div>
              </Panel>

              <PanelResizeHandle className="react-resizable-panels-handle" />

              <Panel
                defaultSize={40}
                minSize={30}
                style={{
                  ...(screens.md ? {} : { minHeight: '350px', flex: 'none' }),
                }}
              >
                <div className="results-panel">
                  <FilterSection />

                  <Affix
                    offsetTop={24}
                    className="results-panel-affix"
                    style={{
                      display: screens.md ? 'block' : 'none',
                    }}
                  >
                    <Card
                      bordered={false}
                      bodyStyle={{
                        padding: 0,
                        maxHeight: screens.md ? cardMaxHeight : 'calc(50vh)',
                        minHeight: screens.md ? 'auto' : '250px',
                        overflow: 'auto',
                        transition: 'max-height 0.1s linear',
                      }}
                    >
                      {/* このCard内にはフラグメントリストのみを表示 */}
                      {/* 以前ここにあったボタン用のdivは削除 */}
                      <div className="fragments-list">
                        {getFilteredAndSortedFragments().map(fragment => (
                          <div
                            key={fragment.id}
                            className="fragment-card interactive"
                            data-fragment-id={fragment.id}
                            onClick={() => handleDrawerOpen(fragment)}
                            onMouseEnter={() =>
                              handleHeaderHover(
                                true,
                                fragment.block_position?.ids,
                                true,
                              )
                            }
                            onMouseLeave={() => handleHeaderHover(false)}
                            style={{
                              cursor: 'pointer',
                              position: 'relative',
                            }}
                          >
                            <div
                              className="fragment-number-badge"
                              style={{
                                position: 'absolute',
                                top: '0',
                                left: '0',
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                color: '#777',
                                width: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                zIndex: 10,
                              }}
                            >
                              #{fragment.order_index + 1}
                            </div>
                            <div
                              className="fragment-header"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px 16px',
                              }}
                            >
                              <Progress
                                type="circle"
                                percent={Math.round(
                                  (fragment.factuality || 0) * 100,
                                )}
                                size={24}
                                strokeColor={(() => {
                                  if (fragment.result) {
                                    return '#d9d9d9';
                                  } else if (
                                    fragment.deep_check_status === 'completed'
                                  ) {
                                    return 'url(#deepCheckGradient)';
                                  } else {
                                    return getAccuracyColor(
                                      Math.round(
                                        (fragment.factuality || 0) * 100,
                                      ),
                                    );
                                  }
                                })()}
                                format={percent => {
                                  const reliabilityValue = percent;
                                  const isDeepChecked =
                                    fragment.deep_check_status === 'completed';
                                  const completedColor = fragment.result
                                    ? '#8f8f8f' // format内のテキスト色も完了時はグレー系
                                    : undefined;

                                  if (reliabilityValue === 100) {
                                    return (
                                      <CheckOutlined
                                        style={{
                                          fontSize: '11px',
                                          color:
                                            completedColor ||
                                            (isDeepChecked
                                              ? undefined
                                              : getAccuracyColor(
                                                  reliabilityValue,
                                                )),
                                          background:
                                            !completedColor && isDeepChecked
                                              ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                                              : undefined,
                                          WebkitBackgroundClip:
                                            !completedColor && isDeepChecked
                                              ? 'text'
                                              : undefined,
                                          WebkitTextFillColor:
                                            !completedColor && isDeepChecked
                                              ? 'transparent'
                                              : undefined,
                                        }}
                                      />
                                    );
                                  }
                                  if (reliabilityValue === 0) {
                                    return (
                                      <span
                                        style={{
                                          fontSize: '11px',
                                          color: '#F5222D',
                                          fontWeight: 'bold',
                                          background:
                                            !completedColor && isDeepChecked
                                              ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                                              : undefined,
                                          WebkitBackgroundClip:
                                            !completedColor && isDeepChecked
                                              ? 'text'
                                              : undefined,
                                          WebkitTextFillColor:
                                            !completedColor && isDeepChecked
                                              ? 'transparent'
                                              : undefined,
                                        }}
                                      >
                                        ?
                                      </span>
                                    );
                                  }
                                  if (isDeepChecked) {
                                    return (
                                      <span
                                        style={{
                                          background: completedColor
                                            ? undefined
                                            : 'linear-gradient(45deg, #722ed1, #eb2f96)',
                                          WebkitBackgroundClip: completedColor
                                            ? undefined
                                            : 'text',
                                          WebkitTextFillColor: completedColor
                                            ? undefined
                                            : 'transparent',
                                          fontSize: '11px',
                                          fontWeight: 'bold',
                                          color: completedColor,
                                        }}
                                      >
                                        {reliabilityValue}
                                      </span>
                                    );
                                  }
                                  return (
                                    <span
                                      style={{
                                        color:
                                          completedColor ||
                                          getAccuracyColor(reliabilityValue),
                                        fontWeight: 'bold',
                                        fontSize: '11px',
                                      }}
                                    >
                                      {reliabilityValue}
                                    </span>
                                  );
                                }}
                              />
                              <div
                                className="fragment-text"
                                style={{
                                  flex: 1,
                                  fontWeight: '500',
                                  // ★変更点: 完了なら取り消し線とグレー文字
                                  ...(fragment.result && {
                                    textDecoration: 'line-through',
                                    color: '#8c8c8c',
                                  }),
                                }}
                              >
                                {fragment.fragment_text}
                              </div>
                              <RightOutlined
                                style={{
                                  fontSize: '12px',
                                  color: '#8c8c8c',
                                  opacity: 1,
                                  transition: 'opacity 0.5s ease-in-out',
                                }}
                              />
                            </div>
                            <svg
                              style={{
                                width: 0,
                                height: 0,
                                position: 'absolute',
                              }}
                            >
                              <defs>
                                <linearGradient
                                  id="deepCheckGradient"
                                  x1="0%"
                                  y1="0%"
                                  x2="100%"
                                  y2="100%"
                                >
                                  <stop offset="0%" stopColor="#722ed1" />
                                  <stop offset="100%" stopColor="#eb2f96" />
                                </linearGradient>
                              </defs>
                            </svg>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </Affix>

                  {/* モバイル表示用 */}
                  {!screens.md && (
                    <Card
                      bordered={false}
                      bodyStyle={{
                        padding: 0,
                        maxHeight: 'calc(50vh)',
                        minHeight: '250px',
                        overflow: 'auto',
                      }}
                    >
                      <div className="fragments-list">
                        {getFilteredAndSortedFragments().map(fragment => (
                          <div
                            key={fragment.id}
                            className="fragment-card interactive"
                            data-fragment-id={fragment.id}
                            onClick={() => handleDrawerOpen(fragment)}
                            style={{ cursor: 'pointer', position: 'relative' }}
                          >
                            <div
                              className="fragment-number-badge"
                              style={{
                                position: 'absolute',
                                top: '0',
                                left: '0',
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                color: '#777',
                                width: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                zIndex: 10,
                              }}
                            >
                              #{fragment.order_index + 1}
                            </div>
                            <div
                              className="fragment-header"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px 16px',
                              }}
                            >
                              <Progress
                                type="circle"
                                percent={Math.round(
                                  (fragment.factuality || 0) * 100,
                                )}
                                size={24}
                                strokeWidth={12}
                                strokeColor={(() => {
                                  if (fragment.result) {
                                    return '#d9d9d9';
                                  } else if (
                                    fragment.deep_check_status === 'completed'
                                  ) {
                                    return 'url(#deepCheckGradient)';
                                  } else {
                                    return getAccuracyColor(
                                      Math.round(
                                        (fragment.factuality || 0) * 100,
                                      ),
                                    );
                                  }
                                })()}
                                format={percent => {
                                  const reliabilityValue = percent;
                                  const isDeepChecked =
                                    fragment.deep_check_status === 'completed';
                                  const completedColor = fragment.result
                                    ? '#8f8f8f' // format内のテキスト色も完了時はグレー系
                                    : undefined;

                                  if (reliabilityValue === 100) {
                                    return (
                                      <CheckOutlined
                                        style={{
                                          fontSize: '11px',
                                          color:
                                            completedColor ||
                                            (isDeepChecked
                                              ? undefined
                                              : getAccuracyColor(
                                                  reliabilityValue,
                                                )),
                                          background:
                                            !completedColor && isDeepChecked
                                              ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                                              : undefined,
                                          WebkitBackgroundClip:
                                            !completedColor && isDeepChecked
                                              ? 'text'
                                              : undefined,
                                          WebkitTextFillColor:
                                            !completedColor && isDeepChecked
                                              ? 'transparent'
                                              : undefined,
                                        }}
                                      />
                                    );
                                  }
                                  if (reliabilityValue === 0) {
                                    return (
                                      <span
                                        style={{
                                          fontSize: '11px',
                                          color: '#F5222D',
                                          fontWeight: 'bold',
                                          background:
                                            !completedColor && isDeepChecked
                                              ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                                              : undefined,
                                          WebkitBackgroundClip:
                                            !completedColor && isDeepChecked
                                              ? 'text'
                                              : undefined,
                                          WebkitTextFillColor:
                                            !completedColor && isDeepChecked
                                              ? 'transparent'
                                              : undefined,
                                        }}
                                      >
                                        ?
                                      </span>
                                    );
                                  }
                                  if (isDeepChecked) {
                                    return (
                                      <span
                                        style={{
                                          background: completedColor
                                            ? undefined
                                            : 'linear-gradient(45deg, #722ed1, #eb2f96)',
                                          WebkitBackgroundClip: completedColor
                                            ? undefined
                                            : 'text',
                                          WebkitTextFillColor: completedColor
                                            ? undefined
                                            : 'transparent',
                                          fontSize: '11px',
                                          fontWeight: 'bold',
                                          color: completedColor,
                                        }}
                                      >
                                        {reliabilityValue}
                                      </span>
                                    );
                                  }
                                  return (
                                    <span
                                      style={{
                                        color:
                                          completedColor ||
                                          getAccuracyColor(reliabilityValue),
                                        fontWeight: 'bold',
                                        fontSize: '11px',
                                      }}
                                    >
                                      {reliabilityValue}
                                    </span>
                                  );
                                }}
                              />
                              <div
                                className="fragment-text"
                                style={{
                                  flex: 1,
                                  fontWeight: '500',
                                  // ★変更点: 完了なら取り消し線とグレー文字
                                  ...(fragment.result && {
                                    textDecoration: 'line-through',
                                    color: '#8c8c8c',
                                  }),
                                }}
                              >
                                {fragment.fragment_text}
                              </div>
                              <RightOutlined
                                style={{
                                  fontSize: '12px',
                                  color: '#8c8c8c',
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>
              </Panel>
            </PanelGroup>
          </div>
        </Layout>
      ) : (
        // ロゴ表示モード（フルレイアウト）
        <Layout style={{ minHeight: '100vh' }}>
          <Layout.Header
            style={{
              background: '#fff',
              padding: '0 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {isAuthenticated && userProfile ? (
              <>
                {screens.xl ? (
                  <a
                    href="https://magicss.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="logo mobile-logo"
                  >
                    <span>Ignite creativity with..</span>magicss
                  </a>
                ) : null}
                <AuthenticatedMenu
                  userProfile={userProfile}
                  userRole={userRole}
                  isMobile={!screens.xl}
                />
              </>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <a
                  href="https://magicss.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="logo mobile-logo"
                  style={{ pointerEvents: 'auto !important' }}
                >
                  <span>Ignite creativity with..</span>magicss
                </a>
                <div
                  style={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Text style={{ marginRight: '16px' }}>
                    ようこそ、ゲストさん！
                  </Text>
                  <Button
                    type="primary"
                    onClick={showRegistrationModal}
                    style={{
                      background:
                        'linear-gradient(90deg, #0072ff 0%, #00d4ff 100%)',
                      border: 'none',
                    }}
                  >
                    無料登録する
                  </Button>
                </div>
              </div>
            )}
          </Layout.Header>

          <Layout.Content
            style={{
              background: '#fff',
              padding: screens.xl ? '40px 40px 80px' : '16px',
              margin: screens.xl ? '24px 24px' : '16px 0',
              minHeight: 280,
            }}
          >
            <div
              style={{ maxWidth: '1500px', margin: '0 auto', width: '100%' }}
            >
              <Row gutter={24}>
                <Col xs={24} lg={14}>
                  <Breadcrumb style={{ margin: '16px 0' }}>
                    <Breadcrumb.Item>ファクトチェック</Breadcrumb.Item>
                    <Breadcrumb.Item>検証結果</Breadcrumb.Item>
                  </Breadcrumb>
                  <Title
                    level={2}
                    style={{ margin: 0 }}
                    className="gradient-text"
                  >
                    検証結果
                  </Title>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'rgba(0, 0, 0, 0.45)',
                      marginTop: '16px',
                    }}
                  >
                    <Space wrap>
                      <span>実行日時： {formatDateTime(task?.created_at)}</span>
                    </Space>
                  </div>
                </Col>
                <Col xs={24} lg={10}>
                  {/* 統計情報セクション */}
                  <Row gutter={[16, 16]} align="middle" wrap>
                    {/* 文字数 */}
                    <Col xs={12} sm={6} md={6} lg={6}>
                      <div
                        style={{
                          textAlign: 'center',
                          height: screens.xs ? '60px' : '76px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            fontSize: screens.xs ? '12px' : '13px',
                            color: 'rgba(0, 0, 0, 0.45)',
                            height: screens.xs ? '18px' : '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          文字数
                          <Tooltip title="検証対象の原稿の総文字数です。">
                            <QuestionCircleOutlined
                              style={{
                                marginLeft: '4px',
                                fontSize: '12px',
                                color: 'rgba(0, 0, 0, 0.45)',
                              }}
                            />
                          </Tooltip>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: screens.xs ? '36px' : '46px',
                          }}
                        >
                          <div
                            style={{
                              fontSize: screens.xs ? '18px' : '24px',
                              fontWeight: '500',
                              lineHeight: '1',
                              display: 'flex',
                              alignItems: 'flex-end',
                            }}
                          >
                            <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>
                              {calculateCharacterCount(
                                editorContent,
                              ).toLocaleString() || '0'}
                            </span>
                            <span
                              style={{
                                fontSize: screens.xs ? '12px' : '14px',
                                marginLeft: '2px',
                                color: 'rgba(0, 0, 0, 0.45)',
                                lineHeight: '1',
                              }}
                            >
                              文字
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>
                    {/* 検証項目 */}
                    <Col xs={12} sm={6} md={6} lg={6}>
                      <div
                        style={{
                          textAlign: 'center',
                          height: screens.xs ? '60px' : '76px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            fontSize: screens.xs ? '12px' : '13px',
                            color: 'rgba(0, 0, 0, 0.45)',
                            height: screens.xs ? '18px' : '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          検証項目
                          <Tooltip title="AIが検出した検証すべき事実的な記述の数です。">
                            <QuestionCircleOutlined
                              style={{
                                marginLeft: '4px',
                                fontSize: '12px',
                                color: 'rgba(0, 0, 0, 0.45)',
                              }}
                            />
                          </Tooltip>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: screens.xs ? '36px' : '46px',
                          }}
                        >
                          <div
                            style={{
                              fontSize: screens.xs ? '18px' : '24px',
                              fontWeight: '500',
                              lineHeight: '1',
                              display: 'flex',
                              alignItems: 'flex-end',
                            }}
                          >
                            <span
                              style={{ color: '#8f8f8f', marginRight: '2px' }}
                            >
                              {stats.completed || 0}
                            </span>
                            <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>
                              {' '}
                              / {stats.total}
                            </span>
                            <span
                              style={{
                                fontSize: screens.xs ? '12px' : '14px',
                                marginLeft: '2px',
                                color: 'rgba(0, 0, 0, 0.45)',
                                lineHeight: '1',
                              }}
                            >
                              件
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>
                    {/* 検出ソース */}
                    <Col xs={12} sm={6} md={6} lg={6}>
                      <div
                        style={{
                          textAlign: 'center',
                          height: screens.xs ? '60px' : '76px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            fontSize: screens.xs ? '12px' : '13px',
                            color: 'rgba(0, 0, 0, 0.45)',
                            height: screens.xs ? '18px' : '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          検出ソース
                          <Tooltip title="検証に使用された情報源の数です。信頼性の高いソースほど優先的に使用されます。">
                            <QuestionCircleOutlined
                              style={{
                                marginLeft: '4px',
                                fontSize: '12px',
                                color: 'rgba(0, 0, 0, 0.45)',
                              }}
                            />
                          </Tooltip>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: screens.xs ? '36px' : '46px',
                          }}
                        >
                          <div
                            style={{
                              fontSize: screens.xs ? '18px' : '24px',
                              fontWeight: '500',
                              lineHeight: '1',
                              display: 'flex',
                              alignItems: 'flex-end',
                            }}
                          >
                            <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>
                              {stats.sourceCount}
                            </span>
                            <span
                              style={{
                                fontSize: screens.xs ? '12px' : '14px',
                                marginLeft: '2px',
                                color: 'rgba(0, 0, 0, 0.45)',
                                lineHeight: '1',
                              }}
                            >
                              件
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>
                    {/* 正確性 */}
                    <Col xs={12} sm={6} md={6} lg={6}>
                      <div
                        style={{
                          textAlign: 'center',
                          height: screens.xs ? '60px' : '76px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            fontSize: screens.xs ? '12px' : '13px',
                            color: 'rgba(0, 0, 0, 0.45)',
                            height: screens.xs ? '18px' : '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          正確性
                          <Tooltip title="検証された項目の正確性スコアの平均値です。100に近いほど原稿の事実的な正確性が高いことを示します。">
                            <QuestionCircleOutlined
                              style={{
                                marginLeft: '4px',
                                fontSize: '12px',
                                color: 'rgba(0, 0, 0, 0.45)',
                              }}
                            />
                          </Tooltip>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Progress
                            type="circle"
                            percent={stats.accuracy}
                            size={screens.xs ? 36 : 46}
                            strokeWidth={screens.xs ? 6 : 8}
                            strokeColor={getAccuracyColor(stats.accuracy)}
                            format={percent => (
                              <span
                                style={{
                                  fontSize: screens.xs ? '12px' : '15px',
                                  fontWeight: 'bold',
                                  color: getAccuracyColor(percent),
                                }}
                              >
                                {percent}
                              </span>
                            )}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <div className="fact-check-content" style={{ margin: '24px 0' }}>
                <PanelGroup
                  direction={screens.md ? 'horizontal' : 'vertical'}
                  autoSaveId="fact-check-panels"
                  defaultSizes={[60, 40]}
                >
                  <Panel
                    defaultSize={60}
                    minSize={40}
                    style={{
                      overflow: screens.md ? 'hidden' : 'visible',
                      ...(screens.md
                        ? {}
                        : { minHeight: '400px', flex: 'none' }),
                    }}
                  >
                    <div className="editor-panel">
                      <Editor
                        key={`editor2-${!(task?.share_status === 'password' && !passwordRequired)}`}
                        initialData={editorContent}
                        onReady={handleEditorReady}
                        config={{
                          readOnly: !(
                            task?.share_status === 'password' &&
                            !passwordRequired
                          ),
                          holder: 'fact-check-editor',
                          minHeight: screens.md ? 0 : 350,
                          placeholder: '本文',
                          inlineToolbar: [],
                          tools: {},
                          blockRenderSettings: {
                            useID: true,
                            preserveIDs: true,
                          },
                        }}
                      />
                    </div>
                  </Panel>

                  <PanelResizeHandle className="react-resizable-panels-handle" />

                  <Panel
                    defaultSize={40}
                    minSize={30}
                    style={{
                      ...(screens.md
                        ? {}
                        : { minHeight: '350px', flex: 'none' }),
                    }}
                  >
                    <div className="results-panel">
                      <FilterSection />

                      <Affix
                        offsetTop={24}
                        className="results-panel-affix"
                        style={{
                          display: screens.md ? 'block' : 'none',
                        }}
                      >
                        <Card
                          bordered={false}
                          bodyStyle={{
                            padding: 0,
                            maxHeight: screens.md
                              ? cardMaxHeight
                              : 'calc(50vh)',
                            minHeight: screens.md ? 'auto' : '250px',
                            overflow: 'auto',
                            transition: 'max-height 0.1s linear',
                          }}
                        >
                          <div className="fragments-list">
                            {getFilteredAndSortedFragments().map(fragment => (
                              <div
                                key={fragment.id}
                                className="fragment-card interactive"
                                data-fragment-id={fragment.id}
                                onClick={() => handleDrawerOpen(fragment)}
                                onMouseEnter={() =>
                                  handleHeaderHover(
                                    true,
                                    fragment.block_position?.ids,
                                    true,
                                  )
                                }
                                onMouseLeave={() => handleHeaderHover(false)}
                                style={{
                                  cursor: 'pointer',
                                  position: 'relative',
                                }}
                              >
                                <div
                                  className="fragment-number-badge"
                                  style={{
                                    position: 'absolute',
                                    top: '0',
                                    left: '0',
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    color: '#777',
                                    width: '18px',
                                    height: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '11px',
                                    zIndex: 10,
                                  }}
                                >
                                  #{fragment.order_index + 1}
                                </div>
                                <div
                                  className="fragment-header"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '12px 16px',
                                  }}
                                >
                                  <Progress
                                    type="circle"
                                    percent={Math.round(
                                      (fragment.factuality || 0) * 100,
                                    )}
                                    size={24}
                                    strokeColor={(() => {
                                      const color = fragment.result
                                        ? '#d9d9d9'
                                        : fragment.deep_check_status ===
                                            'completed'
                                          ? 'url(#deepCheckGradient)'
                                          : getAccuracyColor(
                                              Math.round(
                                                (fragment.factuality || 0) *
                                                  100,
                                              ),
                                            );
                                      return color;
                                    })()}
                                    format={percent => {
                                      const reliabilityValue = percent;
                                      const isDeepChecked =
                                        fragment.deep_check_status ===
                                        'completed';
                                      const completedColor = fragment.result
                                        ? '#8f8f8f'
                                        : undefined;

                                      if (reliabilityValue === 100) {
                                        return (
                                          <CheckOutlined
                                            style={{
                                              fontSize: '11px',
                                              color:
                                                completedColor ||
                                                (isDeepChecked
                                                  ? undefined
                                                  : getAccuracyColor(
                                                      reliabilityValue,
                                                    )),
                                              background:
                                                !completedColor && isDeepChecked
                                                  ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                                                  : undefined,
                                              WebkitBackgroundClip:
                                                !completedColor && isDeepChecked
                                                  ? 'text'
                                                  : undefined,
                                              WebkitTextFillColor:
                                                !completedColor && isDeepChecked
                                                  ? 'transparent'
                                                  : undefined,
                                            }}
                                          />
                                        );
                                      }
                                      if (reliabilityValue === 0) {
                                        return (
                                          <span
                                            style={{
                                              fontSize: '11px',
                                              color: '#F5222D',
                                              fontWeight: 'bold',
                                              background:
                                                !completedColor && isDeepChecked
                                                  ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                                                  : undefined,
                                              WebkitBackgroundClip:
                                                !completedColor && isDeepChecked
                                                  ? 'text'
                                                  : undefined,
                                              WebkitTextFillColor:
                                                !completedColor && isDeepChecked
                                                  ? 'transparent'
                                                  : undefined,
                                            }}
                                          >
                                            ?
                                          </span>
                                        );
                                      }
                                      if (isDeepChecked) {
                                        return (
                                          <span
                                            style={{
                                              background: completedColor
                                                ? undefined
                                                : 'linear-gradient(45deg, #722ed1, #eb2f96)',
                                              WebkitBackgroundClip:
                                                completedColor
                                                  ? undefined
                                                  : 'text',
                                              WebkitTextFillColor:
                                                completedColor
                                                  ? undefined
                                                  : 'transparent',
                                              fontSize: '11px',
                                              fontWeight: 'bold',
                                              color: completedColor,
                                            }}
                                          >
                                            {reliabilityValue}
                                          </span>
                                        );
                                      }
                                      return (
                                        <span
                                          style={{
                                            color:
                                              completedColor ||
                                              getAccuracyColor(
                                                reliabilityValue,
                                              ),
                                            fontWeight: 'bold',
                                            fontSize: '11px',
                                          }}
                                        >
                                          {reliabilityValue}
                                        </span>
                                      );
                                    }}
                                  />
                                  <div
                                    className="fragment-text"
                                    style={{
                                      flex: 1,
                                      fontWeight: '500',
                                      // ★変更点: 完了なら取り消し線とグレー文字
                                      ...(fragment.result && {
                                        textDecoration: 'line-through',
                                        color: '#8c8c8c',
                                      }),
                                    }}
                                  >
                                    {fragment.fragment_text}
                                  </div>
                                  <RightOutlined
                                    style={{
                                      fontSize: '12px',
                                      color: '#8c8c8c',
                                      opacity: 1,
                                      transition: 'opacity 0.5s ease-in-out',
                                    }}
                                  />
                                </div>
                                <svg
                                  style={{
                                    width: 0,
                                    height: 0,
                                    position: 'absolute',
                                  }}
                                >
                                  <defs>
                                    <linearGradient
                                      id="deepCheckGradient"
                                      x1="0%"
                                      y1="0%"
                                      x2="100%"
                                      y2="100%"
                                    >
                                      <stop offset="0%" stopColor="#722ed1" />
                                      <stop offset="100%" stopColor="#eb2f96" />
                                    </linearGradient>
                                  </defs>
                                </svg>
                              </div>
                            ))}
                          </div>
                        </Card>
                      </Affix>

                      {!screens.md && (
                        <Card
                          bordered={false}
                          bodyStyle={{
                            padding: 0,
                            maxHeight: 'calc(50vh)',
                            minHeight: '250px',
                            overflow: 'auto',
                          }}
                        >
                          <div className="fragments-list">
                            {getFilteredAndSortedFragments().map(fragment => (
                              <div
                                key={fragment.id}
                                className="fragment-card interactive"
                                data-fragment-id={fragment.id}
                                onClick={() => handleDrawerOpen(fragment)}
                                style={{
                                  cursor: 'pointer',
                                  position: 'relative',
                                }}
                              >
                                <div
                                  className="fragment-number-badge"
                                  style={{
                                    position: 'absolute',
                                    top: '0',
                                    left: '0',
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    color: '#777',
                                    width: '18px',
                                    height: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '11px',
                                    zIndex: 10,
                                  }}
                                >
                                  #{fragment.order_index + 1}
                                </div>
                                <div
                                  className="fragment-header"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '12px 16px',
                                  }}
                                >
                                  <Progress
                                    type="circle"
                                    percent={Math.round(
                                      (fragment.factuality || 0) * 100,
                                    )}
                                    size={24}
                                    strokeWidth={12}
                                    strokeColor={(() => {
                                      const color = fragment.result
                                        ? '#d9d9d9'
                                        : fragment.deep_check_status ===
                                            'completed'
                                          ? 'url(#deepCheckGradient)'
                                          : getAccuracyColor(
                                              Math.round(
                                                (fragment.factuality || 0) *
                                                  100,
                                              ),
                                            );
                                      return color;
                                    })()}
                                    format={percent => {
                                      const reliabilityValue = percent;
                                      const isDeepChecked =
                                        fragment.deep_check_status ===
                                        'completed';
                                      const completedColor = fragment.result
                                        ? '#8f8f8f'
                                        : undefined;

                                      if (reliabilityValue === 100) {
                                        return (
                                          <CheckOutlined
                                            style={{
                                              fontSize: '11px',
                                              color:
                                                completedColor ||
                                                (isDeepChecked
                                                  ? undefined
                                                  : getAccuracyColor(
                                                      reliabilityValue,
                                                    )),
                                              background:
                                                !completedColor && isDeepChecked
                                                  ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                                                  : undefined,
                                              WebkitBackgroundClip:
                                                !completedColor && isDeepChecked
                                                  ? 'text'
                                                  : undefined,
                                              WebkitTextFillColor:
                                                !completedColor && isDeepChecked
                                                  ? 'transparent'
                                                  : undefined,
                                            }}
                                          />
                                        );
                                      }
                                      if (reliabilityValue === 0) {
                                        return (
                                          <span
                                            style={{
                                              fontSize: '11px',
                                              color: '#F5222D',
                                              fontWeight: 'bold',
                                              background:
                                                !completedColor && isDeepChecked
                                                  ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                                                  : undefined,
                                              WebkitBackgroundClip:
                                                !completedColor && isDeepChecked
                                                  ? 'text'
                                                  : undefined,
                                              WebkitTextFillColor:
                                                !completedColor && isDeepChecked
                                                  ? 'transparent'
                                                  : undefined,
                                            }}
                                          >
                                            ?
                                          </span>
                                        );
                                      }
                                      if (isDeepChecked) {
                                        return (
                                          <span
                                            style={{
                                              background: completedColor
                                                ? undefined
                                                : 'linear-gradient(45deg, #722ed1, #eb2f96)',
                                              WebkitBackgroundClip:
                                                completedColor
                                                  ? undefined
                                                  : 'text',
                                              WebkitTextFillColor:
                                                completedColor
                                                  ? undefined
                                                  : 'transparent',
                                              fontSize: '11px',
                                              fontWeight: 'bold',
                                              color: completedColor,
                                            }}
                                          >
                                            {reliabilityValue}
                                          </span>
                                        );
                                      }
                                      return (
                                        <span
                                          style={{
                                            color:
                                              completedColor ||
                                              getAccuracyColor(
                                                reliabilityValue,
                                              ),
                                            fontWeight: 'bold',
                                            fontSize: '11px',
                                          }}
                                        >
                                          {reliabilityValue}
                                        </span>
                                      );
                                    }}
                                  />
                                  <div
                                    className="fragment-text"
                                    style={{
                                      flex: 1,
                                      fontWeight: '500',
                                      // ★変更点: 完了なら取り消し線とグレー文字
                                      ...(fragment.result && {
                                        textDecoration: 'line-through',
                                        color: '#8c8c8c',
                                      }),
                                    }}
                                  >
                                    {fragment.fragment_text}
                                  </div>
                                  <RightOutlined
                                    style={{
                                      fontSize: '12px',
                                      color: '#8c8c8c',
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      )}
                    </div>
                  </Panel>
                </PanelGroup>
              </div>
            </div>
          </Layout.Content>

          {!hideBranding && (
            <Layout.Footer id="footer">
              <Row gutter={[16, 16]} justify={'space-between'}>
                <Col xs={24} xl={24} xxl={16}>
                  <Row className="f_menu">
                    <Col xs={24} md={12} lg={5}>
                      <a href="https://magicss.ai" target="_blank">
                        magicssサービスサイト <ExportOutlined />
                      </a>
                    </Col>
                    <Col xs={24} md={12} lg={5}>
                      <a href="/terms-of-service" target="_blank">
                        サービス利用規約
                      </a>
                    </Col>
                    <Col xs={24} md={12} lg={5}>
                      <a href="/privacy-policy" target="_blank">
                        プライバシーポリシー
                      </a>
                    </Col>
                    <Col xs={24} md={12} lg={5}>
                      <a href="/legal-notice" target="_blank">
                        特定商取引法に基づく表記
                      </a>
                    </Col>
                    <Col xs={24} md={12} lg={4}>
                      <a
                        href="https://magicss.ai/contact"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        お問い合わせ <ExportOutlined />
                      </a>
                    </Col>
                  </Row>
                </Col>
                <Col
                  xs={24}
                  lg={24}
                  xl={24}
                  xxl={4}
                  style={{ textAlign: 'center' }}
                >
                  © 2024{' '}
                  <a href="https://oneword.co.jp" target="_blank">
                    ONEWORD Inc.
                  </a>
                </Col>
              </Row>
            </Layout.Footer>
          )}
        </Layout>
      )}

      {/* ドロワー（変更なし） */}
      <Drawer
        id="fragment-drawer"
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: screens.xs ? '14px' : '16px',
                lineHeight: screens.xs ? '1.4' : '1.5',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                // ★変更点: ドロワー内のタイトルも完了なら取り消し線とグレー文字
                ...(drawerState.fragment?.result && {
                  color: '#8c8c8c',
                }),
              }}
            >
              {drawerState.fragment?.fragment_text}
            </span>
            {drawerState.fragment?.factuality !== null ? (
              <Progress
                type="circle"
                percent={Math.round(drawerState.fragment?.factuality * 100)}
                width={24}
                strokeWidth={12}
                strokeColor={
                  drawerState.fragment?.result // ★変更点: ドロワー内Progressも完了ならグレー
                    ? '#d9d9d9'
                    : drawerState.fragment?.deep_check_status === 'completed'
                      ? 'url(#deepCheckGradient)'
                      : getAccuracyColor(
                          Math.round(drawerState.fragment?.factuality * 100),
                        )
                }
                format={percent => {
                  const reliabilityValue = percent;
                  const isDeepChecked =
                    drawerState.fragment?.deep_check_status === 'completed';
                  const completedColor = drawerState.fragment?.result // ★変更点: ドロワー内Progressも完了ならグレー
                    ? '#8f8f8f'
                    : undefined;

                  if (reliabilityValue === 100) {
                    return (
                      <CheckOutlined
                        style={{
                          fontSize: '11px',
                          color:
                            completedColor ||
                            (isDeepChecked
                              ? undefined
                              : getAccuracyColor(reliabilityValue)),
                          background:
                            !completedColor && isDeepChecked
                              ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                              : undefined,
                          WebkitBackgroundClip:
                            !completedColor && isDeepChecked
                              ? 'text'
                              : undefined,
                          WebkitTextFillColor:
                            !completedColor && isDeepChecked
                              ? 'transparent'
                              : undefined,
                        }}
                      />
                    );
                  }
                  if (reliabilityValue === 0) {
                    return (
                      <span
                        style={{
                          fontSize: '11px',
                          color: '#F5222D', // 赤色に固定
                          fontWeight: 'bold',
                          background:
                            !completedColor && isDeepChecked
                              ? 'linear-gradient(45deg, #722ed1, #eb2f96)'
                              : undefined,
                          WebkitBackgroundClip:
                            !completedColor && isDeepChecked
                              ? 'text'
                              : undefined,
                          WebkitTextFillColor:
                            !completedColor && isDeepChecked
                              ? 'transparent'
                              : undefined,
                        }}
                      >
                        ?
                      </span>
                    );
                  }
                  if (isDeepChecked) {
                    return (
                      <span
                        style={{
                          background: completedColor
                            ? undefined
                            : 'linear-gradient(45deg, #722ed1, #eb2f96)',
                          WebkitBackgroundClip: completedColor
                            ? undefined
                            : 'text',
                          WebkitTextFillColor: completedColor
                            ? undefined
                            : 'transparent',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: completedColor,
                        }}
                      >
                        {reliabilityValue}
                      </span>
                    );
                  }
                  return (
                    <span
                      style={{
                        color:
                          completedColor || getAccuracyColor(reliabilityValue),
                        fontWeight: 'bold',
                        fontSize: '11px',
                      }}
                    >
                      {reliabilityValue}
                    </span>
                  );
                }}
              />
            ) : (
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: '#f0f0f0',
                }}
              />
            )}
          </div>
        }
        placement="right"
        onClose={handleDrawerClose}
        open={drawerState.visible}
        width={screens.md ? 450 : '85%'}
        className="fragment-drawer"
        mask={true}
        maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        styles={{
          header: {
            paddingRight: '48px',
          },
          body: {
            padding: '24px',
            paddingTop: 0,
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
          wrapper: {
            backgroundColor: 'transparent',
          },
          content: {
            background: 'rgba(255, 255, 255, 0.95)',
          },
        }}
      >
        <svg style={{ width: 0, height: 0, position: 'absolute' }}>
          <defs>
            <linearGradient
              id="deepCheckGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#722ed1" />
              <stop offset="100%" stopColor="#eb2f96" />
            </linearGradient>
          </defs>
        </svg>
        <div
          className="fragment-number"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            padding: '4px 8px',
            background: '#f0f0f0',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#666',
            zIndex: 1,
          }}
        >
          #{drawerState.fragment?.order_index + 1}
        </div>

        <div style={{ marginTop: '16px' }}>
          {drawerState.fragment?.reason && (
            <div
              style={{
                marginBottom: '16px',
                color: 'rgba(0, 0, 0, 0.85)',
                background:
                  'linear-gradient(to right, rgba(114, 46, 209, 0.05), rgba(235, 47, 150, 0.05))',
                padding: '16px',
                borderRadius: '8px',
                fontSize: '14px',
                lineHeight: '1.6',
                textAlign: 'justify',
                boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.06)',
              }}
              dangerouslySetInnerHTML={{
                __html: formatReasonWithLinks(drawerState.fragment?.reason),
              }}
            />
          )}

          {drawerState.fragment?.reference_info?.length > 0 && (
            <>
              <Divider orientation="left">
                <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
                  参照ソース
                </span>
                <Tooltip
                  title={
                    <div style={{ maxWidth: '300px' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div
                          style={{
                            fontWeight: 'bold',
                            marginBottom: '8px',
                            color: 'rgba(255, 255, 255, 0.85)',
                          }}
                        >
                          参照ソースについて
                        </div>
                        <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
                          主張の事実確認のために使用した情報源を表示しています。本文中の
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              background:
                                'linear-gradient(to right, rgba(114, 46, 209, 0.1), rgba(235, 47, 150, 0.1))',
                              padding: '0 4px',
                              borderRadius: '4px',
                              margin: '0 2px',
                              fontWeight: '500',
                            }}
                          >
                            [1]
                          </span>
                          などの番号と対応しています。クリックすると元の情報源ページにアクセスできます。
                        </div>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <div
                          style={{
                            fontWeight: 'bold',
                            marginBottom: '8px',
                            color: 'rgba(255, 255, 255, 0.85)',
                          }}
                        >
                          信頼性バーについて
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            lineHeight: '1.5',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Progress
                              percent={85}
                              size="small"
                              showInfo={false}
                              strokeWidth={4}
                              style={{ width: '40px' }}
                              strokeColor={{
                                '0%': '#722ed1',
                                '100%': '#eb2f96',
                              }}
                            />
                            <span
                              style={{
                                fontSize: '10px',
                                color: '#eb2f96',
                                fontWeight: '500',
                                marginLeft: '4px',
                              }}
                            >
                              85%
                            </span>
                          </div>
                          <span>
                            情報源の信頼度を表しています。公式や政府機関情報は高くなります。
                          </span>
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontWeight: 'bold',
                            marginBottom: '8px',
                            color: 'rgba(255, 255, 255, 0.85)',
                          }}
                        >
                          情報ソースの種類
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px',
                          }}
                        >
                          <Tag
                            color="#333333"
                            style={{
                              margin: 0,
                              padding: '0 4px',
                              fontSize: '10px',
                              lineHeight: '16px',
                              color: '#FFFFFF',
                            }}
                          >
                            公式
                          </Tag>
                          <Tag
                            color="#1677ff"
                            style={{
                              margin: 0,
                              padding: '0 4px',
                              fontSize: '10px',
                              lineHeight: '16px',
                            }}
                          >
                            政府機関
                          </Tag>
                          <Tag
                            color="#eb2f96"
                            style={{
                              margin: 0,
                              padding: '0 4px',
                              fontSize: '10px',
                              lineHeight: '16px',
                            }}
                          >
                            企業
                          </Tag>
                          <Tag
                            color="#52c41a"
                            style={{
                              margin: 0,
                              padding: '0 4px',
                              fontSize: '10px',
                              lineHeight: '16px',
                            }}
                          >
                            ニュース
                          </Tag>
                          <Tag
                            color="#fa8c16"
                            style={{
                              margin: 0,
                              padding: '0 4px',
                              fontSize: '10px',
                              lineHeight: '16px',
                            }}
                          >
                            学術
                          </Tag>
                          <Tag
                            color="#722ed1"
                            style={{
                              margin: 0,
                              padding: '0 4px',
                              fontSize: '10px',
                              lineHeight: '16px',
                            }}
                          >
                            コミュニティ
                          </Tag>
                        </div>
                      </div>
                    </div>
                  }
                  placement="topRight"
                  overlayStyle={{ maxWidth: '350px' }}
                >
                  <InfoCircleOutlined
                    style={{
                      marginLeft: '8px',
                      fontSize: '14px',
                      color: 'rgba(0, 0, 0, 0.45)',
                    }}
                  />
                </Tooltip>
              </Divider>
              <List
                id="reference-list"
                dataSource={drawerState.fragment?.reference_info}
                renderItem={(ref, index) => (
                  <List.Item
                    key={index}
                    id={`reference-item-${index}`}
                    style={{
                      border: 'none',
                      padding: '0',
                      borderBottom:
                        index < drawerState.fragment?.reference_info.length - 1
                          ? '1px solid #f0f0f0'
                          : 'none',
                    }}
                  >
                    <a
                      href={ref.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="reference-link"
                      style={{
                        width: '100%',
                        display: 'block',
                        padding: '8px 8px 8px 0',
                        borderRadius: '4px',
                        transition: 'background-color 0.3s ease',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor =
                          'rgba(0, 0, 0, 0.03)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div style={{ width: '100%' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            marginBottom: '4px',
                          }}
                        >
                          <div
                            style={{
                              minWidth: '24px',
                              textAlign: 'center',
                              color: 'rgba(0, 0, 0, 0.65)',
                              fontWeight: '500',
                              background:
                                'linear-gradient(to right, rgba(114, 46, 209, 0.1), rgba(235, 47, 150, 0.1))',
                              borderRadius: '4px',
                              padding: '0 4px',
                              marginTop: '-8px',
                            }}
                          >
                            {index + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            {(ref.keyQuote || ref.source_text) && (
                              <div>
                                <div
                                  className="reference-quote-text"
                                  style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    lineHeight: '1.5',
                                    color: 'rgba(0, 0, 0, 0.75)',
                                    marginBottom: '8px',
                                    position: 'relative',
                                    // *で囲まれている場合の透明度は削除（リスト項目全体を透明にするため）
                                    opacity: 1,
                                    // 通常テキストの場合、引用スタイルを適用
                                    backgroundColor: 'transparent',
                                    paddingLeft:
                                      (ref.source_text &&
                                        ref.source_text.startsWith('*') &&
                                        ref.source_text.endsWith('*')) ||
                                      (ref.keyQuote &&
                                        ref.keyQuote.startsWith('*') &&
                                        ref.keyQuote.endsWith('*'))
                                        ? '8px'
                                        : '20px',
                                  }}
                                >
                                  {/* 通常テキストの場合は引用アイコンを追加 */}
                                  {!(
                                    (ref.source_text &&
                                      ref.source_text.startsWith('*') &&
                                      ref.source_text.endsWith('*')) ||
                                    (ref.keyQuote &&
                                      ref.keyQuote.startsWith('*') &&
                                      ref.keyQuote.endsWith('*'))
                                  ) && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        top: '-11px',
                                        left: '-8px',
                                        color: 'rgba(114, 46, 209, 0.1)',
                                        fontSize: '64px',
                                        fontFamily: 'serif',
                                      }}
                                    >
                                      "
                                    </div>
                                  )}
                                  {/* *がある場合は除去して表示 */}
                                  {ref.source_text
                                    ? ref.source_text.startsWith('*') &&
                                      ref.source_text.endsWith('*')
                                      ? ref.source_text.substring(
                                          1,
                                          ref.source_text.length - 1,
                                        )
                                      : ref.source_text
                                    : ref.keyQuote &&
                                        ref.keyQuote.startsWith('*') &&
                                        ref.keyQuote.endsWith('*')
                                      ? ref.keyQuote.substring(
                                          1,
                                          ref.keyQuote.length - 1,
                                        )
                                      : ref.keyQuote}
                                </div>
                              </div>
                            )}

                            {/* タイトルと日付の行 */}
                            {(ref.page_title ||
                              (ref.date &&
                                !isNaN(new Date(ref.date).getTime()))) && (
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  marginBottom: '4px',
                                  fontSize: '11px',
                                  color: 'rgba(0, 0, 0, 0.45)',
                                }}
                                className="reference-meta-title"
                              >
                                <div
                                  style={{
                                    flex: '1',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    marginRight: '8px',
                                  }}
                                  className="reference-meta-title-text"
                                >
                                  {ref.page_title && ref.page_title.length > 26
                                    ? `${ref.page_title.substring(0, 26)}...`
                                    : ref.page_title}
                                </div>
                                <div
                                  style={{
                                    flexShrink: '0',
                                    textAlign: 'right',
                                  }}
                                  className="reference-meta-title-date"
                                >
                                  {ref.date &&
                                    !isNaN(new Date(ref.date).getTime()) &&
                                    (() => {
                                      try {
                                        const date = new Date(ref.date);
                                        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
                                      } catch (e) {
                                        return null;
                                      }
                                    })()}
                                </div>
                              </div>
                            )}

                            <div
                              className="reference-meta"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <img
                                src={`https://www.google.com/s2/favicons?domain=${new URL(ref.url).hostname}`}
                                alt=""
                                className="reference-favicon"
                              />
                              <div style={{ flex: 1 }}>
                                <div className="reference-title">
                                  {new URL(ref.url).hostname}
                                </div>
                              </div>
                              {ref.type &&
                                [
                                  'official',
                                  'government',
                                  'corporation',
                                  'news',
                                  'academic',
                                  'community',
                                ].includes(ref.type) && (
                                  <Tag
                                    color={
                                      ref.type === 'official'
                                        ? '#333333' // 濃い黒
                                        : ref.type === 'government'
                                          ? '#E6F4FF' // 薄い青
                                          : ref.type === 'corporation'
                                            ? '#FFF0F6' // 薄いピンク
                                            : ref.type === 'news'
                                              ? '#F6FFED' // 薄い緑
                                              : ref.type === 'academic'
                                                ? '#FFF7E6' // 薄いオレンジ
                                                : ref.type === 'community'
                                                  ? '#F0E6FF' // 薄い紫
                                                  : undefined
                                    }
                                    style={{
                                      margin: 0,
                                      padding: '0 4px',
                                      fontSize: '10px',
                                      lineHeight: '16px',
                                      border: 'none',
                                      color:
                                        ref.type === 'official'
                                          ? '#FFFFFF' // 白
                                          : ref.type === 'government'
                                            ? '#1677FF' // 濃い青
                                            : ref.type === 'corporation'
                                              ? '#EB2F96' // 濃いピンク
                                              : ref.type === 'news'
                                                ? '#52C41A' // 濃い緑
                                                : ref.type === 'academic'
                                                  ? '#FA8C16' // 濃いオレンジ
                                                  : ref.type === 'community'
                                                    ? '#722ED1' // 濃い紫
                                                    : undefined,
                                    }}
                                  >
                                    {(() => {
                                      const typeMap = {
                                        'official': '公式',
                                        'government': '政府機関',
                                        'corporation': '企業',
                                        'news': 'ニュース',
                                        'academic': '学術',
                                        'community': 'コミュニティ',
                                      };
                                      return typeMap[ref.type] || '';
                                    })()}
                                  </Tag>
                                )}
                              {ref.trustness && (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}
                                >
                                  <Progress
                                    percent={Math.round(ref.trustness * 100)}
                                    size="small"
                                    showInfo={false}
                                    strokeWidth={4}
                                    style={{ width: '40px' }}
                                    strokeColor={{
                                      '0%': '#722ed1',
                                      '100%': '#eb2f96',
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      color: '#eb2f96',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {Math.round(ref.trustness * 100)}%
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </a>
                  </List.Item>
                )}
              />
            </>
          )}
        </div>
      </Drawer>

      {/* 無料登録モーダル */}
      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={480}
        centered
        maskClosable={true}
        className="registration-modal"
      >
        <div style={{ padding: '16px 0' }}>
          <div
            style={{
              textAlign: 'center',
              marginBottom: '24px',
            }}
          >
            <img
              src="/logo-write.png"
              alt="magicss"
              style={{ height: '42px', marginBottom: '24px' }}
            />
            <Title level={4} style={{ margin: 0, marginBottom: '8px' }}>
              magicssの機能をフル活用しませんか？
            </Title>
            <Text type="secondary">
              無料ユーザー登録すると以下の機能が使えるようになります
            </Text>
          </div>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card
                className="feature-card"
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Space direction="vertical" size={8}>
                  <div
                    style={{
                      color: '#1890ff',
                      fontSize: '24px',
                      marginBottom: '4px',
                    }}
                  >
                    <FileTextOutlined />
                  </div>
                  <div
                    style={{
                      fontWeight: '500',
                      fontSize: '16px',
                    }}
                  >
                    コンテンツ作成
                  </div>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    AIが品質の高い記事を自動生成します。
                  </Text>
                </Space>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                className="feature-card"
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Space direction="vertical" size={8}>
                  <div
                    style={{
                      color: '#52c41a',
                      fontSize: '24px',
                      marginBottom: '4px',
                    }}
                  >
                    <CheckCircleOutlined />
                  </div>
                  <div
                    style={{
                      fontWeight: '500',
                      fontSize: '16px',
                    }}
                  >
                    ファクトチェック
                  </div>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    記事の事実確認を自動で行います。
                  </Text>
                </Space>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                className="feature-card"
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Space direction="vertical" size={8}>
                  <div
                    style={{
                      color: '#fa8c16',
                      fontSize: '24px',
                      marginBottom: '4px',
                    }}
                  >
                    <LineChartOutlined />
                  </div>
                  <div
                    style={{
                      fontWeight: '500',
                      fontSize: '16px',
                    }}
                  >
                    SEO分析
                  </div>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    上位表示するためのSEO分析を行います。
                  </Text>
                </Space>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                className="feature-card"
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                }}
              >
                <Space direction="vertical" size={8}>
                  <div
                    style={{
                      color: '#722ed1',
                      fontSize: '24px',
                      marginBottom: '4px',
                    }}
                  >
                    <FileSearchOutlined />
                  </div>
                  <div
                    style={{
                      fontWeight: '500',
                      fontSize: '16px',
                    }}
                  >
                    盗作チェック
                  </div>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    オリジナリティを保証するチェック機能。
                  </Text>
                </Space>
              </Card>
            </Col>
          </Row>
          <div
            style={{
              marginTop: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Button
              type="primary"
              size="large"
              block
              onClick={handleRegistration}
              style={{ height: '48px', fontSize: '16px' }}
            >
              無料で登録する
            </Button>
            <div
              style={{
                marginTop: '16px',
                fontSize: '12px',
                color: 'rgba(0, 0, 0, 0.45)',
                textAlign: 'center',
              }}
            >
              登録することで、
              <a
                href="https://magicss.ai/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
              >
                利用規約
              </a>
              と
              <a
                href="https://magicss.ai/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                プライバシーポリシー
              </a>
              に同意したものとみなします。
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SharedFactCheck;
